import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { money, useQuery } from '../api.js';
import { SearchField, matchesSearch } from '../controls.js';
import { Avatar } from '../avatar.js';
import { ErrorNote, Pill, Spinner } from '../ui.js';

interface Row {
  id: number; userId: number; avatarUpdatedAt: number | null;
  name: string; initials: string; email: string;
  courseName: string; regulation: string; enrolledOn: string;
  primaryInstructorName: string; stage: number;
  lessonsComplete: number | null; lessonsTotal: number;
  totalHours: number; balanceCents: number; creditCents: number;
}

export function Students(): JSX.Element {
  const { data, loading, error } = useQuery<{ students: Row[]; showGrades: boolean }>('/students');
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const students = useMemo(
    () => (data?.students ?? []).filter((s) => matchesSearch(
      query, s.name, s.email, s.courseName, s.primaryInstructorName, `stage ${s.stage}`,
    )),
    [data, query],
  );

  if (error) return <ErrorNote message={error} />;
  if (!data) return <Spinner />;

  return (
    <div className="page">
      <div className="card">
        <div className="card__head" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <span>
            <span className="card__title">
              Training records · {query ? `${students.length} of ${data.students.length}` : data.students.length}
            </span>
            <span className="card__meta" style={{ display: 'block', marginTop: 2 }}>
              Click a student to open the syllabus and currency
            </span>
          </span>
          <SearchField
            value={query}
            onChange={setQuery}
            label="Search students"
            placeholder="Name, email, course or instructor"
          />
        </div>
        <div className="table__scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Student</th><th>Course</th><th>Instructor</th><th>Stage</th>
                {data.showGrades && <th>Progress</th>}
                <th>Hours</th><th>Balance</th><th />
              </tr>
            </thead>
            <tbody>
              {students.length === 0 && (
                <tr>
                  <td colSpan={8} className="empty">
                    {query
                      ? `Nothing matches "${query}".`
                      : 'No students are assigned to you.'}
                  </td>
                </tr>
              )}
              {students.map((s) => (
                <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/students/${s.id}`)}>
                  <td className="bold">
                    <span className="row" style={{ gap: 9, flexWrap: 'nowrap' }}>
                      <Avatar userId={s.userId} updatedAt={s.avatarUpdatedAt} initials={s.initials} size={26} />
                      {s.name}
                    </span>
                  </td>
                  <td>{s.courseName} <span className="muted">· Part {s.regulation}</span></td>
                  <td>{s.primaryInstructorName}</td>
                  <td><Pill tone="blue">Stage {s.stage}</Pill></td>
                  {data.showGrades && (
                    <td>
                      <span className="row" style={{ gap: 6 }}>
                        <span className="bar" style={{ width: 80 }}>
                          <span style={{ width: `${Math.round(((s.lessonsComplete ?? 0) / Math.max(1, s.lessonsTotal)) * 100)}%` }} />
                        </span>
                        <span className="small muted">{s.lessonsComplete}/{s.lessonsTotal}</span>
                      </span>
                    </td>
                  )}
                  <td className="num">{s.totalHours.toFixed(1)}</td>
                  <td className="num">
                    {s.balanceCents > 0
                      ? <span style={{ color: 'var(--conflict-c)', fontWeight: 700 }}>{money(s.balanceCents)}</span>
                      : s.creditCents > 0 ? <span style={{ color: 'var(--ok)' }}>{money(s.creditCents)} credit</span>
                      : <span className="muted">—</span>}
                  </td>
                  <td className="num"><span className="btn btn--link">Open</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {loading && <div className="small muted" style={{ padding: 8 }}>Refreshing…</div>}
    </div>
  );
}
